"use client";

import { SelectedDayOut, useDayOut } from "@/app/_components/companion/DayOutSelection";
import { DAY_OUT_PARAM, dayOutFromSavedRoute, dayOutParam, dayOutPlannerTiming, dayOutRouteMatches } from "@/lib/companion/day-out";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { RouteEssentials } from "@/app/_components/planner/RouteEssentials";
import { RouteEffortSummary } from "@/app/_components/planner/RouteEffortSummary";
import { RouteJourney } from "@/app/_components/planner/RouteJourney";
import { AdaptiveRouteReview } from "@/app/_components/planner/AdaptiveRouteReview";
import { OfflineJourneyPanel } from "@/app/_components/planner/OfflineJourneyPanel";
import { TimedStopField } from "@/app/_components/planner/TimedStopField";
import { PassCoverageField } from "@/app/_components/planner/PassCoverageField";
import { RouteDownload } from "@/app/_components/planner/RouteDownload";
import GroupJourneyPanel from "@/app/_components/planner/GroupJourneyPanel";
import { readPlannerPreferences, writePlannerPreferences, readSavedRoute, saveSelectedRoute, type SavedRoute } from "@/lib/planner/saved-route";
import type { AdaptiveRequest } from "@/lib/planner/adaptive-types";
import { compactAdaptiveRequest } from "@/lib/planner/adaptive-journey";
import { isSharedPayload } from "@/lib/planner/shared-payload";
import { plannerAvailabilityNotes } from "@/lib/planner/coverage-policy";
import dynamic from "next/dynamic";
import type { PlannerData } from "@/lib/planner/data";
import type { ItinerarySuccess, SharedPlanResponse } from "@/lib/planner/api-types";
import { isClosed } from "@/lib/planner/api-types";
import { resolveSharedPlanSport } from "@/lib/planner/api-types";
import {
  DEFAULT_MTB_PREFERENCES,
  DEFAULT_PREFERENCES,
  type PlannerPreferences,
} from "@/lib/planner/preferences";
import {
  applyDayStyle,
  isValidDayStyle,
  stylePatchPathsFor,
  type DayStyle,
  W,
} from "@/lib/planner/day-styles";
import type { AreaSport } from "@/lib/planner/graph-types";
import { setShallowSearchParams, useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import { PreferencesPanel } from "@/app/_components/planner/PreferencesPanel";
import { OutingSuggestions } from "@/app/_components/planner/OutingSuggestions";
import { useOutingPreferences } from "@/app/_components/planner/useOutingPreferences";
import { ItineraryTimeline } from "@/app/_components/planner/ItineraryTimeline";
import { noScorableSkiTerrain } from "@/lib/planner/empty-state";
import { SharePlan } from "@/app/_components/planner/SharePlan";
import { BikeParkGuidance } from "@/app/_components/planner/BikeParkGuidance";
import { bikeParksForArea } from "@/lib/planner/bikepark-knowledge";
import { PlanEditor } from "@/app/_components/planner/PlanEditor";
import { PlanMyDayEntry } from "@/app/_components/planner/PlanMyDayEntry";
import { PlannerLoadingState } from "@/app/_components/planner/PlannerLoadingState";
import { MountainMapLoading } from "@/app/_components/planner/MountainMapLoading";
import { RightNowCard } from "@/app/_components/planner/RightNowCard";
import { TweakBar } from "@/app/_components/planner/TweakBar";
import {
  mergeRouteAlternativePatch,
  RouteRecommendationPanel,
  swapToRouteB,
} from "@/app/_components/planner/RouteRecommendationPanel";
import type { StyleAlternative } from "@/lib/planner/route-recommendations";
import { plannerConfidenceNotices } from "@/lib/planner/confidence-notices";
import { applyVibe, isValidVibe, type VibeKey } from "@/lib/planner/vibes";
import { isOfflinePackStale, offlinePackBySlug } from "@/lib/native/offline-packs";
import { nowWindowLimitLabel } from "@/lib/planner/now-context";
import { trackPlannerEvent } from "@/lib/planner/analytics-events";
import {
  getInstalledOfflinePack,
  installOfflinePack,
  type StoredOfflinePack,
} from "@/lib/native/offline-pack-store";

const PisteMapPreview = dynamic(
  () => import("@/app/_components/planner/PisteMapPreview").then((m) => m.PisteMapPreview),
  {
    ssr: false,
    loading: () => <MountainMapLoading stage="renderer" />,
  },
);

const OfflinePlannerSnapshotPanel = dynamic(
  () => import("@/app/_components/companion/OfflinePlannerSnapshotPanel").then((m) => m.OfflinePlannerSnapshotPanel),
  {
    ssr: false,
    loading: () => <div className="h-[480px] w-full animate-pulse rounded-[18px] bg-warm-bg md:h-[600px]" />,
  },
);

import { MountainWorkspace } from "@/app/_components/planner/MountainWorkspace";
import { PlannerWorkspace } from "@/app/_components/planner/PlannerWorkspace";

type Props = { slug: string; embedded?: boolean; active?: boolean; sportOverride?: AreaSport };

function hasStartedDay(saved: SavedRoute | null, candidate: ItinerarySuccess, slug: string): boolean {
  return saved?.status === "started" && saved.slug === slug && saved.plan.scoredDate === candidate.scoredDate;
}

function sportFromParam(raw: string | null): AreaSport {
  return raw === "mtb" ? "mtb" : "ski";
}

function uniqueTouchedFields(fields: readonly (string | undefined)[]): string[] {
  const out: string[] = [];
  for (const field of fields) {
    if (!field || out.includes(field)) continue;
    out.push(field);
    if (out.length >= 40) break;
  }
  return out;
}

function touchedFieldsForRequest(
  prefs: PlannerPreferences,
  touchedFields: readonly string[],
): string[] {
  return uniqueTouchedFields([
    ...touchedFields,
    ...stylePatchPathsFor(prefs.dayStyle),
  ]);
}

function touchedFieldsForPatch(patch: Partial<PlannerPreferences>): string[] {
  const fields: string[] = [];
  const topLevel = [
    "dayStyle",
    "date",
    "startTime",
    "endTime",
    "ability",
    "difficultyMode",
    "distance",
    "coffeeStops",
    "apres",
    "group",
    "returnToStart",
    "includeUnknownLifts",
    "startPoint",
    "requiredStop",
    "skiPass",
    "allowBus",
    "poling",
    "treeRuns",
    "ridingStyle",
    "fitness",
    "climbingTolerance",
    "liftAccess",
    "bikeType",
  ] as const;
  for (const field of topLevel) {
    if (field in patch) fields.push(field);
  }
  if (patch.lunch) {
    if ("time" in patch.lunch) fields.push("lunch.time");
    if ("style" in patch.lunch) fields.push("lunch.style");
    if ("price" in patch.lunch) fields.push("lunch.price");
  }
  if ("liftPrefs" in patch && patch.liftPrefs) {
    if ("avoidDrags" in patch.liftPrefs) fields.push("liftPrefs.avoidDrags");
    if ("preferGondolas" in patch.liftPrefs) fields.push("liftPrefs.preferGondolas");
    if ("preferFastLifts" in patch.liftPrefs) fields.push("liftPrefs.preferFastLifts");
    if ("allowedTypes" in patch.liftPrefs && patch.liftPrefs.allowedTypes) {
      if ("drags" in patch.liftPrefs.allowedTypes) fields.push("liftPrefs.allowedTypes.drags");
      if ("gondolas" in patch.liftPrefs.allowedTypes) fields.push("liftPrefs.allowedTypes.gondolas");
      if ("chairs" in patch.liftPrefs.allowedTypes) fields.push("liftPrefs.allowedTypes.chairs");
    }
  }
  if (patch.objectives) {
    for (const key of Object.keys(patch.objectives)) fields.push(`objectives.${key}`);
  }
  return uniqueTouchedFields(fields);
}

function PlannerConfidenceNotices({
  notices,
}: {
  notices: ReturnType<typeof plannerConfidenceNotices>;
}) {
  if (notices.length === 0) return null;
  return (
    <div className="mb-3 space-y-2" data-export-ignore>
      {notices.map((notice) => (
        <p
          key={notice.kind}
          className="rounded-[12px] border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-900"
        >
          {notice.message}
        </p>
      ))}
    </div>
  );
}

export function PlannerApp(props: Props) {
  return <PlannerWorkspace active={props.active}><SelectedDayOut planner /><PlannerAppContent {...props} /></PlannerWorkspace>;
}

function PlannerAppContent({ slug, embedded = false, active = true, sportOverride }: Props) {
  const { raw: dayOutRaw, selection: dayOut } = useDayOut();
  const appliedDayOut = useRef<string | null>(null);
  // ?sport=ski|mtb, read through the shallow hook so the static conditions page
  // is never opted into useSearchParams() (ISR invariant). Defaults to ski.
  const requestedSport = useShallowSearchParam("sport");
  const urlSport = sportOverride ?? sportFromParam(requestedSport);
  const offlinePackParam = useShallowSearchParam("offline-pack");
  const requestedOfflinePack = offlinePackBySlug(offlinePackParam);
  const offlineDemo = useShallowSearchParam("offline-demo") === "1";
  const installDemoPack = useShallowSearchParam("install-demo") === "1";
  const planId = useShallowSearchParam("plan");
  const [sharedSport, setSharedSport] = useState<AreaSport | null>(null);
  const sport = planId && sharedSport ? sharedSport : urlSport;
  const storySport = useShallowSearchParam("storySport");
  const storyType = useShallowSearchParam("storyType");
  const storyId = useShallowSearchParam("storyId");
  const routeHomeRequested = useShallowSearchParam("routeHome") === "1";
  const storySelection = useMemo(() => {
    if (storySport !== null && storySport !== sport) return undefined;
    if (storyType !== "trail" && storyType !== "lift") return undefined;
    if (
      storyId === null
      || storyId.length === 0
      || storyId.length > 200
      || storyId !== storyId.trim()
      || /[\u0000-\u001f\u007f]/u.test(storyId)
    ) return undefined;
    return { kind: storyType, id: storyId } as const;
  }, [sport, storyId, storySport, storyType]);

  const [data, setData] = useState<PlannerData | null>(null);
  const [dataState, setDataState] = useState<"loading" | "ready" | "error">("loading");
  const [offlinePack, setOfflinePack] = useState<StoredOfflinePack | null>(null);

  // Per-sport preference state, so switching sport never has to reset state in
  // an effect: the active preferences are simply selected by the URL sport.
  // Each sport keeps its own edits while the user toggles between them.
  const [skiPrefs, setSkiPrefs] = useState<PlannerPreferences>(DEFAULT_PREFERENCES);
  const [mtbPrefs, setMtbPrefs] = useState<PlannerPreferences>(DEFAULT_MTB_PREFERENCES);
  const [preferencesSlug, setPreferencesSlug] = useState<string | null>(null);
  const preferencesReady = preferencesSlug === slug;
  const resumeSaved = useShallowSearchParam("saved") === "1";
  const resumedSavedRef = useRef(false);
  const [localSaved, setLocalSaved] = useState<SavedRoute | null>(null);
  const skipNextPlan = useRef(false);
  useEffect(() => {
    setSkiPrefs(readPlannerPreferences("ski", slug) ?? DEFAULT_PREFERENCES);
    setMtbPrefs(readPlannerPreferences("mtb", slug) ?? DEFAULT_MTB_PREFERENCES);
    setPreferencesSlug(slug);
    const refreshSaved = () => setLocalSaved(readSavedRoute());
    refreshSaved();
    window.addEventListener("peakwave-route-saved", refreshSaved);
    return () => window.removeEventListener("peakwave-route-saved", refreshSaved);
  }, [slug]);
  useEffect(() => { if (preferencesReady) { writePlannerPreferences(skiPrefs, slug); writePlannerPreferences(mtbPrefs, slug); } }, [preferencesReady, skiPrefs, mtbPrefs, slug]);
  const outingPreferences = useOutingPreferences(slug, sport, sport === "mtb" ? mtbPrefs : skiPrefs, sport === "mtb" ? setMtbPrefs : setSkiPrefs);
  const { prefs, setPrefs } = outingPreferences;
  const [skiTouchedFields, setSkiTouchedFields] = useState<string[]>([]);
  const [mtbTouchedFields, setMtbTouchedFields] = useState<string[]>([]);
  const touchedFields = sport === "mtb" ? mtbTouchedFields : skiTouchedFields;
  const setTouchedFields = sport === "mtb" ? setMtbTouchedFields : setSkiTouchedFields;

  const dayOutTiming = dayOut && data ? dayOutPlannerTiming(dayOut, data.area.timezone) : null;
  const dayOutBlocked = Boolean(dayOutRaw && (!dayOut || dayOut.slug !== slug
    || !["ski", "piste", "mtb"].includes(dayOut.sport)
    || (dayOut.sport === "mtb" ? "mtb" : "ski") !== sport
    || (dayOut.journeyId ? !dayOutRouteMatches(dayOut, localSaved) : data && !dayOutTiming)));
  useEffect(() => {
    if (!dayOut || !dayOutRaw || dayOutBlocked || !data || !preferencesReady || planId || resumeSaved
      || dayOut.journeyId || appliedDayOut.current === dayOutRaw) return;
    const timing = dayOutPlannerTiming(dayOut, data.area.timezone);
    if (!timing) return;
    appliedDayOut.current = dayOutRaw;
    setPrefs((current) => ({ ...current, ...timing }));
    setTouchedFields((current) => [...new Set([...current, "date", "startTime", "endTime"])]);
  }, [data, dayOut, dayOutRaw, dayOutBlocked, planId, preferencesReady, resumeSaved, setPrefs, setTouchedFields]);

  // Entry state machine: "cold" shows the style cards, "plan" shows the mapped
  // day plus tweak bar. Legacy ?vibe= links still adapt to a style and auto-plan.
  // The param is user-controlled, so validate it against the legacy keys before
  // it can reach applyVibe (a bad or cross-sport key must not apply).
  const rawVibeParam = useShallowSearchParam("vibe");
  const vibeParam: VibeKey | null =
    rawVibeParam && isValidVibe(sport, rawVibeParam) ? rawVibeParam : null;
  const [entryMode, setEntryMode] = useState<"cold" | "plan">("cold");
  const [fineTune, setFineTune] = useState(false);
  const [activeStyle, setActiveStyle] = useState<DayStyle | null>(null);

  useEffect(() => {
    if (activeStyle && !isValidDayStyle(sport, activeStyle)) setActiveStyle(null);
  }, [activeStyle, sport]);

  const [plan, setPlan] = useState<ItinerarySuccess | null>(null);
  const [routeChange, setRouteChange] = useState<{ plan: ItinerarySuccess; previous: ItinerarySuccess; preferences: PlannerPreferences; preferenceSnapshot: string; receivedAt: number; savedSnapshot: string | null } | null>(null);
  const latestRoute = useRef({ plan, prefs });
  const crewMapRef = useRef<HTMLDivElement>(null);
  useEffect(() => { latestRoute.current = { plan, prefs }; }, [plan, prefs]);
  const [planning, setPlanning] = useState(false);
  const [pendingStyleKey, setPendingStyleKey] = useState<DayStyle | null>(null);
  const [editing, setEditing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const hasGeneratedPlanRef = useRef(false);

  const routeHomeStartedRef = useRef(false);

  useEffect(() => {
    hasGeneratedPlanRef.current = false;
    abortRef.current?.abort();
    setPlan(null);
    setRouteChange(null);
    setEntryMode("cold");
    setNowMode(null);
  }, [slug, sport]);

  // "Next few hours" mode: set when the user planned from a live position.
  // Preference tweaks re-plan in the same mode; one tap re-plans from a fresh
  // fix and the remaining clock.
  const [nowMode, setNowMode] = useState<{
    horizonHours: 2 | 3 | 4;
    coords: { lat: number; lng: number };
  } | null>(null);
  const [nowError, setNowError] = useState<string | null>(null);

  const markTouchedFields = useCallback(
    (paths: readonly string[]) => {
      if (paths.length === 0) return;
      setTouchedFields((current) => uniqueTouchedFields([...current, ...paths]));
    },
    [setTouchedFields],
  );
  const markTouched = useCallback(
    (path?: string) => {
      if (path) markTouchedFields([path]);
    },
    [markTouchedFields],
  );

  // Shared plan: when ?plan=<id> is present, show that saved plan read-only.
  const [shared, setShared] = useState<
    Pick<ItinerarySuccess, "itinerary" | "route" | "stops" | "scoredDate"> | null
  >(null);
  const [sharedState, setSharedState] = useState<"idle" | "loading" | "error">("idle");
  useEffect(() => {
    if (!planId) {
      setShared(null);
      setSharedSport(null);
      setSharedState("idle");
      return;
    }
    let cancelled = false;
    setSharedState("loading");
    fetch(`/api/planner/share?id=${encodeURIComponent(planId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((resp: SharedPlanResponse | null) => {
        if (cancelled) return;
        if (isSharedPayload(resp?.payload)) {
          setSharedSport(resolveSharedPlanSport(resp, urlSport));
          setShared({
            itinerary: resp.payload.itinerary as ItinerarySuccess["itinerary"],
            route: resp.payload.route as ItinerarySuccess["route"],
            stops: resp.payload.stops as ItinerarySuccess["stops"],
            scoredDate: resp.planDate ?? "",
          });
          setSharedState("idle");
        } else {
          setSharedState("error");
        }
      })
      .catch(() => !cancelled && setSharedState("error"));
    return () => {
      cancelled = true;
    };
  }, [planId, urlSport]);

  // Map base data + availability.
  const [dataSport, setDataSport] = useState<AreaSport | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (requestedOfflinePack && offlineDemo) {
      setData(null);
      setDataSport(null);
      setDataState("error");
      return;
    }
    setDataState("loading");
    fetch(`/api/planner/${encodeURIComponent(slug)}/data?sport=${sport}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: PlannerData | null) => {
        if (cancelled) return;
        if (d && d.area) {
          setData(d);
          setDataSport(sport);
          setDataState("ready");
        } else {
          setDataSport(null);
          setDataState("error");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDataSport(null);
          setDataState("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, sport, requestedOfflinePack, offlineDemo]);

  // Refresh stored operating reports while an active journey is visible. This
  // reads the same bounded map snapshot as initial load, never live sources.
  useEffect(() => {
    if (localSaved?.slug !== slug || localSaved.status !== "started" || localSaved.preferences.sport !== sport || dataState !== "ready") return;
    let cancelled = false;
    let pending = false;
    let lastChecked = Date.now();
    const refresh = () => {
      if (!active || document.visibilityState !== "visible" || !navigator.onLine || pending || Date.now() - lastChecked < 5 * 60_000) return;
      lastChecked = Date.now(); pending = true;
      void fetch(`/api/planner/${encodeURIComponent(slug)}/data?sport=${sport}`).then(async (response) => {
        if (!response.ok) return;
        const updated = await response.json() as PlannerData;
        if (!cancelled && updated.area && Array.isArray(updated.lifts)) setData(updated);
      }).catch(() => undefined).finally(() => { pending = false; });
    };
    const timer = window.setInterval(refresh, 60_000);
    document.addEventListener("visibilitychange", refresh);
    return () => { cancelled = true; window.clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [active, dataState, localSaved?.slug, localSaved?.status, localSaved?.preferences.sport, slug, sport]);

  useEffect(() => {
    let cancelled = false;
    if (!requestedOfflinePack) {
      setOfflinePack(null);
      return;
    }
    void getInstalledOfflinePack(requestedOfflinePack.slug)
      .then((record) => {
        if (cancelled) return;
        if (record && !isOfflinePackStale(record, Date.now())) {
          setOfflinePack(record);
          return null;
        }
        if (installDemoPack) return installOfflinePack(requestedOfflinePack);
        setOfflinePack(null);
        return null;
      })
      .then((record) => {
        if (!cancelled && record) setOfflinePack(record);
      })
      .catch(() => {
        if (!cancelled) setOfflinePack(null);
      });
    return () => {
      cancelled = true;
    };
  }, [requestedOfflinePack, installDemoPack]);

  const canPlan = data != null && !dayOutBlocked;
  const availabilityNotes = data ? plannerAvailabilityNotes(data.area, sport) : [];

  const requestPlan = useCallback(
    (p: PlannerPreferences, now?: { horizonHours: 2 | 3 | 4; coords: { lat: number; lng: number } }) => {
      if (JSON.stringify(p) !== JSON.stringify(latestRoute.current.prefs)) return;
      abortRef.current?.abort();
      setRouteChange(null);
      const ac = new AbortController();
      const previous = latestRoute.current.plan;
      const preferenceSnapshot = JSON.stringify(p);
      const saved = readSavedRoute();
      const needsReview = !!previous && (!!previous.groupContext || (saved?.status === "started" && saved.slug === slug && saved.preferences.sport === sport));
      const savedSnapshot = needsReview ? JSON.stringify(saved) : null;
      abortRef.current = ac;
      setPlanning(true);
      setNowError(null);
      trackPlannerEvent("planner_generation_started", { slug, sport, dayStyle: p.dayStyle });
      const generationStartedAt = performance.now();
      let timedOut = false;
      const timeout = window.setTimeout(() => { timedOut = true; ac.abort(); }, 45_000);
      const body = { preferences: p, touchedFields: touchedFieldsForRequest(p, touchedFields), sport, ...(now ? { mode: "next-hours", horizonHours: now.horizonHours, start: now.coords } : {}) };
      void fetch(`/api/planner/${encodeURIComponent(slug)}/itinerary`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), signal: ac.signal })
        .then(async (response) => {
          const result = await response.json().catch(() => null);
          if (ac.signal.aborted || JSON.stringify(latestRoute.current.prefs) !== preferenceSnapshot) return;
          if (!response.ok) throw new Error(result?.message ?? "We could not update your route. Please try again.");
          if (!result) throw new Error("The route response was incomplete. Please try again.");
          if (isClosed(result)) throw new Error(result.message);
          if (!Array.isArray(result.itinerary?.legs) || !Array.isArray(result.route) || !result.routeExplanation) throw new Error("The route response was incomplete. Please try again.");
          if (result.itinerary.legs.length === 0) throw new Error(result.itinerary.warnings?.at(-1) ?? "No suitable route fits these settings. Try another start or a shorter window.");
          if (needsReview && previous) {
            if (latestRoute.current.plan !== previous) return;
            setRouteChange({ plan: result as ItinerarySuccess, previous, preferences: result.preferences ?? p, preferenceSnapshot, receivedAt: Date.now(), savedSnapshot });
          } else setPlan(result as ItinerarySuccess);
          trackPlannerEvent("planner_generation_succeeded", { slug, sport, dayStyle: p.dayStyle, durationMs: performance.now() - generationStartedAt });
          if (hasGeneratedPlanRef.current) trackPlannerEvent("planner_replanned", { slug, sport, dayStyle: p.dayStyle, source: now ? "next-hours" : "day" });
          hasGeneratedPlanRef.current = true;
          if (now) { setNowMode((current) => current?.horizonHours === now.horizonHours ? current : now); setEntryMode("plan"); }
        })
        .catch((error: unknown) => {
          if (ac.signal.aborted && !timedOut) return;
          setNowError(timedOut ? "This is taking longer than expected. Please try again." : error instanceof Error ? error.message : "We could not update your route. Please try again.");
          trackPlannerEvent("planner_generation_failed", { slug, sport, dayStyle: p.dayStyle, action: timedOut ? "timeout" : "request-or-no-route", durationMs: performance.now() - generationStartedAt });
        })
        .finally(() => {
          window.clearTimeout(timeout);
          if (abortRef.current !== ac) return;
          setPlanning(false);
          setPendingStyleKey(null);
        });
    }, [slug, sport, touchedFields],
  );

  const requestRouteChange = useCallback((next: PlannerPreferences, adaptive?: AdaptiveRequest) => {
    if (!plan) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPlanning(true); setNowError(null); setRouteChange(null);
    const previous = plan;
    const saved = readSavedRoute();
    const savedSnapshot = saved ? JSON.stringify(saved) : null;
    const timeout = window.setTimeout(() => controller.abort(), 45_000);
    const path = adaptive ? "adaptive" : "itinerary";
    const body = adaptive ? compactAdaptiveRequest(adaptive) : { preferences: next, sport, touchedFields: touchedFieldsForRequest(next, touchedFields) };
    trackPlannerEvent("planner_generation_started", { slug, sport, dayStyle: next.dayStyle, source: "journey-review" });
    const generationStartedAt = performance.now();
    void fetch(`/api/planner/${encodeURIComponent(slug)}/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), signal: controller.signal }).then(async (response) => {
      const result = await response.json().catch(() => null) as ItinerarySuccess & { message?: string } | null;
      if (controller.signal.aborted) return;
      if (!response.ok || !result || !isSharedPayload(result) || !result.area || !result.routeExplanation || !Array.isArray(result.baseNodes)) throw new Error(result?.message ?? "No suitable change was found. Your current route is still available.");
      if (latestRoute.current.plan !== previous || JSON.stringify(latestRoute.current.prefs) !== JSON.stringify(prefs)) return;
      setRouteChange({ plan: result, previous, preferences: result.preferences ?? next, preferenceSnapshot: JSON.stringify(prefs), receivedAt: Date.now(), savedSnapshot });
      trackPlannerEvent("planner_generation_succeeded", { slug, sport, dayStyle: next.dayStyle, source: "journey-review", durationMs: performance.now() - generationStartedAt });
    }).catch((error: unknown) => {
      if (abortRef.current !== controller) return;
      setNowError(controller.signal.aborted ? "The change took too long to check. Your current route is still available." : error instanceof Error ? error.message : "We could not check this change. Your current route is still available.");
      trackPlannerEvent("planner_generation_failed", { slug, sport, dayStyle: next.dayStyle, source: "journey-review", durationMs: performance.now() - generationStartedAt });
    }).finally(() => { window.clearTimeout(timeout); if (abortRef.current === controller) setPlanning(false); });
  }, [plan, prefs, slug, sport, touchedFields]);

  const acceptRouteChange = () => {
    if (!routeChange || routeChange.previous !== plan) { setRouteChange(null); return; }
    if (routeChange.preferenceSnapshot !== JSON.stringify(prefs)) { setNowError("Your settings changed while this suggestion was open. Review the change again with your current settings."); setRouteChange(null); return; }
    if (Date.now() - routeChange.receivedAt > 2 * 60_000) { setNowError("This suggested change is over two minutes old. Check it again for a current departure time."); setRouteChange(null); return; }
    const saved = readSavedRoute();
    if (JSON.stringify(saved) !== (routeChange.savedSnapshot ?? "null")) { setNowError("Your saved route or progress changed while this suggestion was open. Check the change again from your current step."); setRouteChange(null); return; }
    if (hasStartedDay(saved, routeChange.plan, slug) && !routeChange.plan.adaptiveContext) { setNowError("A day is already in progress. Use the journey controls to change the remaining route, or finish that journey before starting a new day plan."); return; }
    if (saved?.slug === slug && JSON.stringify(saved.plan.itinerary.legs) === JSON.stringify(plan.itinerary.legs)) {
      const replacement = saveSelectedRoute(slug, routeChange.preferences, routeChange.plan, { journeyScope: routeChange.plan.adaptiveContext ? "remaining-route" : "selected-route", initialStatus: saved.status === "started" ? "started" : "saved" });
      if (!replacement) { setNowError("Device storage could not keep the changed route. Your previous saved route is still available."); return; }
      // An accepted remainder is a new saved journey. Keep the URL selection
      // aligned so the day-out guard and Companion can still open it.
      setShallowSearchParams({ [DAY_OUT_PARAM]: dayOutParam(dayOutFromSavedRoute(replacement)) });
    }
    skipNextPlan.current = true;
    setNowMode(null); setPrefs(routeChange.preferences); setActiveStyle(routeChange.preferences.dayStyle); setPlan(routeChange.plan); setRouteChange(null); setNowError(null);
    markTouchedFields(["startPoint", "finishPoint", "returnToStart", "startTime", "endTime", "ability", "difficultyMode", "excludedLiftIds"]);
    trackPlannerEvent("planner_replanned", { slug, sport, dayStyle: routeChange.preferences.dayStyle, source: "accepted-journey-change" });
  };

  const reviewGroupRoute = (candidate: ItinerarySuccess) => {
    abortRef.current?.abort(); setPlanning(false);
    if (hasStartedDay(readSavedRoute(), candidate, slug) && !candidate.adaptiveContext) {
      setNowError("A day is already in progress. Use the journey controls to change the remaining route, or finish that journey before starting a new group day.");
      return;
    }
    const next = candidate.preferences ?? prefs;
    if (plan) {
      setRouteChange({ plan: candidate, previous: plan, preferences: next, preferenceSnapshot: JSON.stringify(prefs), receivedAt: Math.min(Date.now(), Date.parse(candidate.generatedAt ?? "") || Date.now()), savedSnapshot: JSON.stringify(readSavedRoute()) });
      return;
    }
    skipNextPlan.current = true;
    setPrefs(next); setActiveStyle(next.dayStyle); setPlan(candidate); setEntryMode("plan"); setNowMode(null); setNowError(null);
  };

  const applyEditedPlan = (candidate: ItinerarySuccess) => {
    if (plan?.groupContext || readSavedRoute()?.status === "started") reviewGroupRoute(candidate);
    else setPlan(candidate);
  };

  // One tap: a fresh fix, the same horizon, the same brain.
  const replanFromHere = useCallback(() => {
    if (!nowMode || !("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        requestPlan(prefs, {
          horizonHours: nowMode.horizonHours,
          coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        }),
      () => setNowError("We could not refresh your location. Check location access and try again."),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
    );
  }, [nowMode, prefs, requestPlan]);

  const applyRouteAlternative = useCallback(
    (patch: Partial<PlannerPreferences>) => {
      const next = mergeRouteAlternativePatch(prefs, patch);
      setPrefs(next);
      markTouchedFields(touchedFieldsForPatch(patch));
      setActiveStyle(null);
      setEntryMode("plan");
    },
    [markTouchedFields, prefs, setPrefs],
  );

  const planStyleAlternative = useCallback(
    (alternative: StyleAlternative) => {
      const next = applyDayStyle(prefs, alternative.styleKey, touchedFields);
      setPendingStyleKey(alternative.styleKey);
      setPrefs(next);
      markTouched("dayStyle");
      setActiveStyle(alternative.styleKey);
      setEntryMode("plan");
      trackPlannerEvent("planner_style_selected", {
        slug,
        sport,
        dayStyle: alternative.styleKey,
        source: "style-alternative",
      });
    },
    [markTouched, prefs, setPrefs, slug, sport, touchedFields],
  );

  const swapRouteB = useCallback(() => {
    if (!plan?.routeB) return;
    setPlan((current) => current ? swapToRouteB(current) : current);
    trackPlannerEvent("planner_route_b_swapped", {
      slug,
      sport,
      dayStyle: prefs.dayStyle,
    });
  }, [plan?.routeB, prefs.dayStyle, slug, sport]);

  const pickStyle = useCallback(
    (key: DayStyle) => {
      const next = applyDayStyle(prefs, key, touchedFields);
      setPrefs(next);
      markTouched("dayStyle");
      setActiveStyle(key);
      setEntryMode("plan");
      trackPlannerEvent("planner_style_selected", {
        slug,
        sport,
        dayStyle: key,
        source: "entry",
      });
    },
    [markTouched, prefs, setPrefs, slug, sport, touchedFields],
  );

  const buildYourOwn = useCallback(() => {
    setFineTune(true);
    setActiveStyle(null);
    setEntryMode("plan");
  }, []);

  useEffect(() => {
    routeHomeStartedRef.current = false;
  }, [routeHomeRequested, slug, sport]);

  // Tracker deep-link: reacquire the position in the planner, keep exact GPS
  // coordinates out of URLs and browser history, then use the normal
  // preference-aware engine. Next-hours plans finish at a graph lift-base
  // node, while returnToStart would incorrectly return to the current fix.
  useEffect(() => {
    if (
      !routeHomeRequested ||
      !canPlan ||
      planId ||
      routeHomeStartedRef.current
    ) {
      return;
    }
    routeHomeStartedRef.current = true;
    if (!("geolocation" in navigator)) {
      setNowError(
        "We could not get your location. Choose a lift base to plan your return.",
      );
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next = { ...prefs, returnToStart: false };
        setPrefs(next);
        setNowMode({
          horizonHours: 3,
          coords: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
        });
        setEntryMode("plan");
      },
      () => {
        setNowError(
          "We could not refresh your location. Choose a lift base to plan your return.",
        );
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 30_000 },
    );
  }, [
    canPlan,
    planId,
    prefs,
    routeHomeRequested,
    setPrefs,
  ]);

  // Plan on first load (when in season) and whenever preferences change,
  // debounced so dragging a toggle does not fire a request per keystroke.
  // Skipped while viewing a shared plan. In next-hours mode the same effect
  // requests a fresh position when settings change in next-hours mode.
  useEffect(() => {
    if (!canPlan || planId || entryMode !== "plan" || !preferencesReady) return;
    if (skipNextPlan.current) { skipNextPlan.current = false; return; }
    const id = window.setTimeout(() => {
      if (nowMode) replanFromHere();
      else requestPlan(prefs);
    }, 350);
    return () => window.clearTimeout(id);
  }, [canPlan, prefs, requestPlan, replanFromHere, planId, entryMode, nowMode, preferencesReady]);

  const openSavedRoute = useCallback((saved: SavedRoute, source: "saved-button" | "saved-link") => {
    skipNextPlan.current = true;
    setPrefs(saved.preferences);
    setActiveStyle(saved.preferences.dayStyle);
    setPlan(saved.plan);
    setEntryMode("plan");
    trackPlannerEvent("planner_route_reopened", {
      slug: saved.slug, sport: saved.preferences.sport, dayStyle: saved.preferences.dayStyle, source,
    });
  }, [setPrefs]);

  useEffect(() => {
    if (!resumeSaved || (dayOut && !dayOutRouteMatches(dayOut, localSaved)) || resumedSavedRef.current || !canPlan || !localSaved || localSaved.slug !== slug || localSaved.preferences.sport !== sport) return;
    resumedSavedRef.current = true;
    openSavedRoute(localSaved, "saved-link");
  }, [canPlan, dayOut, localSaved, openSavedRoute, resumeSaved, slug, sport]);

  // Legacy deep-link ?vibe=: adapt once map data is available, then auto-plan.
  useEffect(() => {
    if (!canPlan || planId || !vibeParam || entryMode === "plan") return;
    const next = applyVibe(prefs, vibeParam);
    setPrefs(next);
    setActiveStyle(next.dayStyle);
    setEntryMode("plan");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on deep-link
  }, [canPlan, planId, vibeParam]);

  if (dayOutBlocked && dataState !== "loading") return <p role="status" className="rounded-md border border-border-light bg-white p-5 text-sm text-navy">This selection cannot be used with the current planner. Review fresh conditions or clear the selection above to choose a new day.</p>;

  if (dataState === "loading" || (dataState === "ready" && dataSport !== sport)) {
    return <MountainMapLoading stage="data" />;
  }
  if ((dataState === "error" || !data) && requestedOfflinePack) {
    if (offlinePack) {
      return <OfflinePlannerSnapshotPanel pack={offlinePack} />;
    }
    return (
      <div className="rounded-[16px] border border-border-light bg-warm-bg p-6">
        <h1 className="font-serif text-[26px] font-bold text-navy">
          {requestedOfflinePack.areaName} offline planner
        </h1>
        <p className="mt-2 text-[14px] leading-relaxed text-text-muted">
          This pack is not downloaded or is stale on this device. Download it before switching
          to airplane mode.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-[12px] bg-white p-4">
            <p className="text-[12px] font-semibold uppercase tracking-[0.8px] text-text-muted">
              Graph
            </p>
            <p className="mt-1 text-[18px] font-bold text-navy">
              {requestedOfflinePack.contents.graph ? "Packaged" : "Missing"}
            </p>
          </div>
          <div className="rounded-[12px] bg-white p-4">
            <p className="text-[12px] font-semibold uppercase tracking-[0.8px] text-text-muted">
              Conditions
            </p>
            <p className="mt-1 text-[18px] font-bold text-navy">
              {requestedOfflinePack.contents.conditions ? "Cached today" : "Missing"}
            </p>
          </div>
          <div className="rounded-[12px] bg-white p-4">
            <p className="text-[12px] font-semibold uppercase tracking-[0.8px] text-text-muted">
              Basemap
            </p>
            <p className="mt-1 text-[18px] font-bold text-navy">
              {requestedOfflinePack.contents.mapTiles ? "Bundled" : "Missing"}
            </p>
          </div>
        </div>
        <Link
          href="/companion/offline"
          className="mt-5 inline-flex min-h-[44px] items-center rounded-sm bg-navy px-4 text-[13px] font-semibold text-white no-underline hover:bg-navy-hover"
        >
          Back to offline packs
        </Link>
      </div>
    );
  }
  if ((dataState === "error" || !data) && localSaved?.slug === slug && localSaved.preferences.sport === sport && !planId) {
    return <div>{nowError && <p role="alert" className="mb-3 text-sm text-amber-900">{nowError}</p>}<p role="status" className="mb-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">Showing your saved route from {localSaved.plan.scoredDate}. Status and weather are not live. Follow local signs and the official map.</p><RouteEffortSummary plan={localSaved.plan} sport={sport} /><RouteJourney slug={slug} plan={localSaved.plan} preferences={localSaved.preferences} busy={false} onAdjust={() => setNowError("Reconnect to check a new route.")} /><OfflineJourneyPanel slug={slug} plan={localSaved.plan} preferences={localSaved.preferences} /><ItineraryTimeline itinerary={localSaved.plan.itinerary} scoredDate={localSaved.plan.scoredDate} sport={sport} /></div>;
  }
  if (dataState === "error" || !data) {
    return (
      <div className="rounded-[16px] border border-border-light bg-warm-bg p-6 text-center">
        <p className="text-[14px] text-text-muted">
          {sport === "mtb"
            ? "We could not load the trail map right now."
            : "We could not load the piste map right now."}{" "}
          <Link href={`/conditions/${slug}`} className="text-gold no-underline hover:underline">
            Back to conditions
          </Link>
        </p>
      </div>
    );
  }

  // Shared plan view: a saved plan, read-only, with a CTA to make your own.
  const confidenceNotices = plannerConfidenceNotices(data, sport);

  const renderControls = () => {
  if (planId) {
    if (sharedState === "loading") {
      return <div className="h-[480px] w-full animate-pulse rounded-[18px] bg-warm-bg md:h-[600px]" />;
    }
    if (sharedState === "error" || !shared) {
      return (
        <div className="rounded-[16px] border border-border-light bg-warm-bg p-6 text-center">
          <p className="text-[14px] text-text-muted">
            This shared plan could not be found.{" "}
            <Link
              href={`/conditions/${slug}/planner${sport === "mtb" ? "?sport=mtb" : ""}`}
              className="text-gold no-underline hover:underline"
            >
              {sport === "mtb" ? "Plan your own ride" : "Plan your own day"}
            </Link>
          </p>
        </div>
      );
    }
    return (
      <div className={embedded ? "space-y-5" : "grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]"}>
        {!embedded && <div>
          <PisteMapPreview data={data} scoreLensAvailable={false} routeLegs={shared.route} itinerary={shared.itinerary} routeDate={shared.scoredDate} stops={shared.stops} places={data.places} tall sport={sport} ability={prefs.ability} initialSelection={storySelection} />
        </div>}
        <div>
          <p className="mb-3 rounded-xl bg-amber-50 p-4 text-sm text-amber-900">Shared route for {shared.scoredDate}. This is a saved snapshot. Check a fresh route before setting off.</p>
          <PlannerConfidenceNotices notices={confidenceNotices} />
          <ItineraryTimeline itinerary={shared.itinerary} scoredDate={shared.scoredDate} sport={sport} />
          <div className="mt-4 rounded-[16px] border border-gold/25 bg-gold/[0.05] p-5 text-center">
            <p className="text-[14px] font-semibold text-navy">
              {sport === "mtb" ? "Like the look of this ride?" : "Like the look of this day?"}
            </p>
            <Link
              href={`/conditions/${slug}/planner${sport === "mtb" ? "?sport=mtb" : ""}`}
              className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-sm bg-gold px-6 text-sm font-semibold text-white no-underline hover:bg-gold-light"
            >
              {sport === "mtb"
                ? `Plan your own ride in ${data.area.name} →`
                : `Plan your own day in ${data.area.name} →`}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const treeDataAvailable = data.trails.some((t) => t.treeCoverFraction != null);
  const baseNodes = plan?.baseNodes ?? data.area.baseNodes ?? [];
  const hasBuses = plan?.hasBuses ?? false;
  const placesAvailable = plan?.placesAvailable ?? false;
  const isMtb = sport === "mtb";
  // Curated bike parks whose gravity trails are not yet on the open map: shown
  // as honest sector guidance, emphasised when the generated plan is thin.
  const bikeParks = isMtb ? bikeParksForArea(data.memberSlugs) : [];
  const planThin = !!plan && plan.itinerary.totals.skiKm < 3;
  // Zero scores establish no scorable ski terrain, not a specific cause such as
  // missing snow. Wind, storms and other forecast limits may also contribute.
  // The helper skips Closed runs and runs without a score (null daily), so a
  // closed resort or a withheld score never reads as poor forecast conditions.
  const conditionsThin = noScorableSkiTerrain(sport, data.trailScoresAvailable, data.trails);
  const emptyCopy = isMtb
    ? "Set your preferences to map your ride."
    : "Set your preferences to map your day.";

  if (entryMode === "cold") {
    return (
      <div className={embedded ? "space-y-5" : "grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"}>
        <div className="space-y-5">
          {offlinePack ? (
            <div className="rounded-[14px] border border-gold/25 bg-gold/[0.06] p-4">
              <p className="text-[13px] leading-relaxed text-navy">
                {offlinePack.areaName} offline pack is available. Live planner data is loaded
                while connected; cached pack data is the fallback for dead zones.
              </p>
            </div>
          ) : null}
          <PlannerConfidenceNotices notices={confidenceNotices} />
          {availabilityNotes.map((note) => <p key={note} className="mt-2 text-sm text-amber-900">{note}</p>)}
          <RouteEssentials prefs={prefs} bases={baseNodes} onChange={(next, field) => { setPrefs(next); markTouched(field); }} />
          <PassCoverageField prefs={prefs} data={data} slug={slug} onChange={(next, field) => { setPrefs(next); markTouched(field); }} />
          <TimedStopField prefs={prefs} bases={baseNodes} onChange={(next, field) => { setPrefs(next); markTouched(field); }} />
          {localSaved?.slug === slug && localSaved.preferences.sport === sport && <button className="min-h-[44px] rounded-xl border border-border-light bg-white px-4 text-sm font-semibold text-navy" onClick={() => openSavedRoute(localSaved, "saved-button")}>Open saved route from {localSaved.plan.scoredDate}</button>}
          <PlanMyDayEntry sport={sport} onPickStyle={pickStyle} onBuildYourOwn={buildYourOwn} busy={planning} />
          <details className="rounded-2xl border border-border-light bg-white p-4">
            <summary className="flex min-h-[44px] cursor-pointer items-center text-sm font-semibold text-navy">Already out? Plan from my current location</summary>
            <p className="mb-3 text-sm text-text-muted">Use your location for the next few hours, starting now.</p>
            <RightNowCard sport={sport} busy={planning} errorMessage={nowError}
              onPlanNow={(horizonHours, coords) => requestPlan(prefs, { horizonHours, coords })} onFallbackToDay={buildYourOwn} />
          </details>
          <GroupJourneyPanel key={`${slug}:${sport}`} slug={slug} prefs={prefs} bases={baseNodes} timezone={data.area.timezone} onAdopt={reviewGroupRoute} onOpenCrewMap={() => crewMapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} />
          {bikeParks.length > 0 ? <BikeParkGuidance parks={bikeParks} /> : null}
        </div>
        {!embedded && <div ref={crewMapRef}>
          <PisteMapPreview data={data} scoreLensAvailable places={data.places} tall sport={sport} ability={prefs.ability} initialSelection={storySelection} />
        </div>}
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-5" : "grid items-start gap-5 lg:grid-cols-[minmax(0,1.1fr)_minmax(340px,.9fr)]"}>
      {plan && <div className="order-first lg:col-span-2"><RouteEffortSummary plan={plan} sport={sport} /><RouteDownload key={`${plan.generatedAt}:${plan.selectedRoute}:${plan.routeExplanation.summary}`} slug={slug} plan={plan} preferences={plan.preferences ?? prefs} /></div>}
      <div className="lg:col-span-2"><GroupJourneyPanel key={`${slug}:${sport}`} slug={slug} prefs={prefs} bases={baseNodes} timezone={data.area.timezone} onAdopt={reviewGroupRoute} onOpenCrewMap={() => crewMapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} /></div>
      {nowError && <div role="alert" className="order-first rounded-xl bg-amber-50 p-4 text-sm text-amber-900 lg:col-span-2"><p>{nowError}</p>{plan && <p className="mt-2 font-semibold">The route below still uses your previous settings.</p>}<button className="mt-2 min-h-[44px] rounded-lg border border-amber-300 px-4 font-semibold" onClick={() => nowMode ? replanFromHere() : requestPlan(prefs)}>Try again</button></div>}
      {/* Preferences */}
      <details open={fineTune} onToggle={(event) => setFineTune(event.currentTarget.open)} className="rounded-[18px] border border-border-light bg-white p-4 md:p-5 lg:col-span-2">
        <summary className="flex min-h-[44px] cursor-pointer items-center font-semibold text-navy">Adjust your preferences</summary>
        {outingPreferences.context && outingPreferences.ownerKey && <OutingSuggestions key={`${outingPreferences.ownerKey}:${sport}`} context={outingPreferences.context} ownerKey={outingPreferences.ownerKey} prefs={prefs} busy={planning} temporary={outingPreferences.temporary} onClear={outingPreferences.clear} onApply={(next, fields, expected, ownerKey) => { outingPreferences.apply(next, expected, ownerKey); markTouchedFields(fields); setActiveStyle(null); }} />}
        <div className={embedded ? "mt-3 space-y-4" : "mt-3 grid gap-4 lg:grid-cols-2"}>
        <div className="lg:col-span-2">
        <TweakBar
          prefs={prefs}
          sport={sport}
          touchedFields={touchedFields}
          activeStyle={activeStyle}
          onChange={(next, touchedPath, selectedStyle) => {
            setPrefs(next);
            markTouched(touchedPath);
            if (selectedStyle) {
              setActiveStyle(selectedStyle);
              trackPlannerEvent("planner_style_selected", {
                slug,
                sport,
                dayStyle: selectedStyle,
                source: "tweak-bar",
              });
            }
          }}
        />
        </div>
        <div className="space-y-4">
        <RouteEssentials prefs={prefs} bases={baseNodes} onChange={(next, field) => { setPrefs(next); markTouched(field); }} />
        <PassCoverageField prefs={prefs} data={data} slug={slug} onChange={(next, field) => { setPrefs(next); markTouched(field); }} />
        <TimedStopField prefs={prefs} bases={baseNodes} onChange={(next, field) => { setPrefs(next); markTouched(field); }} />
        </div>
        <PreferencesPanel
          prefs={prefs}
          onChange={(p, touchedPath) => {
            setPrefs(p);
            setActiveStyle(null);
            if (typeof touchedPath === "string") markTouched(touchedPath);
            else if (touchedPath) markTouchedFields(touchedPath);
          }}
          baseNodes={baseNodes}
          hasBuses={hasBuses}
          treeDataAvailable={treeDataAvailable}
          placesAvailable={placesAvailable}
          loading={planning}
          onToggleObjective={(objective, on) => {
            setPrefs({
              ...prefs,
              objectives: { ...prefs.objectives, [objective]: on ? W : 0 },
            });
            setActiveStyle(null);
            markTouched(`objectives.${objective}`);
          }}
          fineTuneDefaultOpen={fineTune}
        />
        </div>
      </details>

      {/* Map */}
      {!embedded && <div className="min-w-0 lg:sticky lg:top-24">
        <div className="mb-3">
          {availabilityNotes.map((note) => <p key={note} className="mt-2 text-sm text-amber-900">{note}</p>)}
        </div>
        <PlannerConfidenceNotices notices={confidenceNotices} />
        <div ref={crewMapRef}><PisteMapPreview data={data} scoreLensAvailable routeLegs={plan?.route} itinerary={plan?.itinerary} routeDate={plan?.scoredDate} stops={plan?.stops} places={data.places} tall sport={sport} ability={prefs.ability} initialSelection={storySelection} /></div>
      </div>}

      {/* Timeline */}
      <div className="min-w-0">
        {plan ? (
          <RouteRecommendationPanel
            plan={plan}
            explanation={plan.routeExplanation}
            bestRunsToday={plan.bestRunsToday}
            routeB={plan.routeB}
            selectedRoute={plan.selectedRoute}
            styleAlternatives={plan.styleAlternatives}
            sport={sport}
            pendingStyleKey={pendingStyleKey}
            onSwapRouteB={swapRouteB}
            onPlanStyle={planStyleAlternative}
          />
        ) : null}
        {routeChange && <AdaptiveRouteReview key={`${routeChange.plan.generatedAt}:${routeChange.receivedAt}`} plan={routeChange.plan} previous={routeChange.previous} busy={planning} onAccept={acceptRouteChange} onDiscard={() => { const restored = routeChange.previous.preferences ?? prefs; skipNextPlan.current = restored !== prefs; setPrefs(restored); setActiveStyle(restored.dayStyle); setRouteChange(null); }} />}
        {plan && <RouteJourney key={`${plan.generatedAt ?? plan.scoredDate}:${plan.selectedRoute ?? "A"}`} slug={slug} plan={plan} preferences={plan.preferences ?? prefs} busy={planning} onAdjust={requestRouteChange} lifts={data.lifts} />}
        {plan && <OfflineJourneyPanel slug={slug} plan={plan} preferences={plan.preferences ?? prefs} data={data} />}

        {plan ? (
          <>
            {plan.nowContext ? (
              <div className="mb-3 rounded-[14px] border border-gold/30 bg-gold/[0.06] px-4 py-3" data-export-ignore>
                <p className="text-[13px] leading-snug text-navy">
                  <span className="font-semibold">From your position</span>
                  {plan.nowContext.snapLabel
                    ? ` (${plan.nowContext.snapDistanceM} m from ${plan.nowContext.snapLabel})`
                    : ` (snapped ${plan.nowContext.snapDistanceM} m to the network)`}
                  {" · "}
                  {plan.nowContext.startTime} until {plan.nowContext.endTime}
                  {nowWindowLimitLabel(plan.nowContext)
                    ? ` · ${nowWindowLimitLabel(plan.nowContext)}`
                    : ""}
                  {plan.nowContext.delayedToOpen ? " · starts with the first lifts" : ""}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={replanFromHere}
                    disabled={planning}
                    className="inline-flex min-h-[44px] items-center rounded-sm bg-navy px-4 text-[13px] font-semibold text-white transition-colors hover:bg-navy/90 disabled:opacity-60"
                  >
                    {planning ? "Re-planning..." : "Re-plan from here"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNowMode(null);
                      requestPlan(prefs);
                    }}
                    disabled={planning}
                    className="inline-flex min-h-[44px] items-center rounded-sm border border-navy/20 bg-white px-4 text-[13px] font-semibold text-navy transition-colors hover:border-gold/40 disabled:opacity-60"
                  >
                    Plan the full day instead
                  </button>
                </div>
                {nowError ? (
                  <p className="mt-2 text-[12.5px] text-amber-800">{nowError}</p>
                ) : null}
              </div>
            ) : null}
            <div className="rounded-[18px] bg-warm-bg p-1">
              <ItineraryTimeline itinerary={plan.itinerary} scoredDate={plan.scoredDate} sport={sport} conditionsThin={conditionsThin} unavailableReason={plan.routeExplanation.unavailableReason} />
            </div>
            {plan.itinerary.legs.length > 0 && !plan.preferences?.requiredStop ? (
              <div className="mt-3" data-export-ignore>
                <button
                  type="button"
                  onClick={() => setEditing((e) => !e)}
                  aria-expanded={editing}
                  className="inline-flex min-h-[44px] items-center rounded-sm border border-border-light bg-white px-4 text-[13px] font-semibold text-navy transition-colors hover:border-gold/40"
                >
                  {editing ? "Done editing" : "Edit this plan"}
                </button>
                {editing ? (
                  <div className="mt-3">
                    <PlanEditor
                      slug={slug}
                      plan={plan}
                      preferences={plan.preferences ?? prefs}
                      touchedFields={touchedFieldsForRequest(plan.preferences ?? prefs, touchedFields)}
                      onApply={applyEditedPlan}
                      onApplyAlternative={applyRouteAlternative}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            <SharePlan
              slug={slug}
              areaName={data.area.name}
              preferences={plan.preferences ?? prefs}
              plan={plan}
            />
          </>
        ) : planning ? (
          <PlannerLoadingState sport={sport} />
        ) : (
          <div className="rounded-[16px] border border-border-light bg-warm-bg p-6 text-center text-[13px] text-text-muted">
            {emptyCopy}
          </div>
        )}
        {bikeParks.length > 0 ? (
          <div className="mt-4" data-export-ignore>
            <BikeParkGuidance parks={bikeParks} planThin={planThin} />
          </div>
        ) : null}
      </div>
    </div>
  );
  };

  if (!embedded) return renderControls();
  const displayed = planId ? shared : plan;
  return <MountainWorkspace data={data} sport={sport} hasRoute={Boolean(displayed?.itinerary.legs.length)} planning={planning}
    map={<div ref={crewMapRef}><PisteMapPreview data={data} scoreLensAvailable={!planId && data.trailScoresAvailable}
      routeLegs={displayed?.route} itinerary={displayed?.itinerary} routeDate={displayed?.scoredDate}
      stops={displayed?.stops} places={data.places} tall immersive companionSharedMap active={active} sport={sport}
      ability={prefs.ability} initialSelection={storySelection} /></div>}>
    {renderControls()}
  </MountainWorkspace>;
}
